import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

/** Normalize timezone like +0200 -> +02:00 so Date.parse is reliable */
function normalizeOffset(ts) {
  return ts.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
}

/** Optional: readable label; keep raw string if you prefer */
function fmt(ts) {
  // return ts; // keep raw string if you like
  const d = new Date(Date.parse(normalizeOffset(ts)));
  return isNaN(d) ? ts : d.toLocaleString(); // or toLocaleDateString()
}

function buildGroupedByTimestampOptions(
  items,
  { stacked = false, percent = false } = {},
) {
  const elements = items || [];

  // 1) Collect all unique timestamps
  const tsSet = new Set();
  elements.forEach((el) =>
    (el.EnergyData || []).forEach(
      (p) => p?.timestamp && tsSet.add(p.timestamp),
    ),
  );
  const timestamps = Array.from(tsSet);

  // 2) Sort timestamps chronologically
  timestamps.sort((a, b) => {
    const da = Date.parse(normalizeOffset(a));
    const db = Date.parse(normalizeOffset(b));
    if (!isNaN(da) && !isNaN(db)) return da - db;
    return a.localeCompare(b);
  });

  // 3) Build a quick lookup per element: { timestamp -> sum }
  const series = elements.map((el) => {
    const name = (el.EnergySummary && el.EnergySummary.Name) || 'Unnamed';
    const map = new Map();
    (el.EnergyData || []).forEach((p) => {
      const key = p?.timestamp;
      if (!key) return;
      const y = Number(p?.sum) || 0;
      // If duplicates exist for a timestamp, add them up:
      map.set(key, (map.get(key) || 0) + y);
    });
    // Align data with the global timestamp list
    const data = timestamps.map((ts) => {
      const v = map.get(ts);
      return v == null ? null : v;
    });
    return { type: 'column', name, data };
  });

  return {
    chart: { type: 'column' },
    title: { text: '' },
    xAxis: {
      categories: timestamps.map(fmt),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: { text: percent ? 'Share (%)' : 'Forbrug' },
      // labels: { format: percent ? '{value}%' : '{value}' },
    },
    legend: { enabled: true },
    plotOptions: {
      series: {
        stacking: stacked ? (percent ? 'percent' : 'normal') : undefined,
        dataLabels: {
          enabled: false,
          formatter: function () {
            if (percent && this.percentage != null)
              return `${this.percentage.toFixed(0)}%`;
            return this.y == null ? '' : this.y.toLocaleString();
          },
        },
      },
      column: {
        pointPadding: 0.1,
        borderWidth: 0,
        groupPadding: stacked ? 0.1 : 0.2,
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        // `this` is a Highcharts.TooltipFormatterContextObject for shared tooltips
        const header = `<b>${this.x}</b>`;
        const lines = this.points.map((p) => {
          const y = p.y;
          const pct =
            p.percentage != null ? ` (${p.percentage.toFixed(1)}%)` : '';
          return `<span style="font-weight:600">${p.series.name}:</span> ${y == null ? '-' : y.toLocaleString()}${pct}`;
        });
        return `${header}<br/>${lines.join('<br/>')}`;
      },
    },
    credits: { enabled: false },
    series,
  };
}

export default function DistributionAlarmBarChart({
  items,
  stacked = false,
  percent = false,
}) {
  const options = buildGroupedByTimestampOptions(items, { stacked, percent });
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
